import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["container", "textarea", "editorPane", "previewPane", "preview", "toggleBtn"]

  static values = {
    // i18n translations
    previewOnlyText: { type: String, default: "仅预览" },
    editText: { type: String, default: "编辑" },
    previewText: { type: String, default: "预览" }
  }

  connect() {
    this.mode = "edit" // edit, split, preview
  }

  togglePreview() {
    const panes = this.containerTarget.querySelector(".editor-panes")

    if (this.mode === "edit") {
      this.mode = "split"
      panes.classList.add("split-view")
      panes.classList.remove("preview-only")
      this.previewPaneTarget.classList.remove("hidden")
      this.toggleBtnTarget.textContent = this.previewOnlyTextValue
      this.toggleBtnTarget.classList.add("active")
      this.updatePreview()
    } else if (this.mode === "split") {
      this.mode = "preview"
      panes.classList.remove("split-view")
      panes.classList.add("preview-only")
      this.toggleBtnTarget.textContent = this.editTextValue
    } else {
      this.mode = "edit"
      panes.classList.remove("split-view", "preview-only")
      this.previewPaneTarget.classList.add("hidden")
      this.toggleBtnTarget.textContent = this.previewTextValue
      this.toggleBtnTarget.classList.remove("active")
    }
  }

  updatePreview() {
    if (this.mode === "edit") return

    const markdown = this.textareaTarget.value
    const html = this.renderMarkdown(markdown)
    this.previewTarget.innerHTML = this.sanitizeHtml(html)
  }

  renderMarkdown(text) {
    if (!text) return ""

    // 保护代码块
    const codeBlocks = []
    text = text.replace(/```(\w*)\n([\s\S]*?)```/g, (match, lang, code) => {
      codeBlocks.push({ lang, code })
      return `{{CODE_BLOCK_${codeBlocks.length - 1}}}`
    })

    // 行内代码
    const inlineCodes = []
    text = text.replace(/`([^`]+)`/g, (match, code) => {
      inlineCodes.push(code)
      return `{{INLINE_CODE_${inlineCodes.length - 1}}}`
    })

    const lines = text.split("\n")
    const htmlLines = []
    let inList = false
    let isOrderedList = false
    let listBuffer = []

    const flushList = () => {
      if (listBuffer.length === 0) return
      const tag = isOrderedList ? "ol" : "ul"
      const items = listBuffer.map(item => `<li>${item}</li>`).join("\n")
      htmlLines.push(`<${tag}>\n${items}\n</${tag}>`)
      listBuffer = []
    }

    const processInline = (text) => {
      text = this.escapeHtml(text)

      // 还原行内代码
      text = text.replace(/\{\{INLINE_CODE_(\d+)\}\}/g, (match, idx) => {
        return `<code>${this.escapeHtml(inlineCodes[parseInt(idx)])}</code>`
      })

      // 粗体
      text = text.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      text = text.replace(/__(.+?)__/g, "<strong>$1</strong>")

      // 斜体
      text = text.replace(/\*(.+?)\*/g, "<em>$1</em>")
      text = text.replace(/_(.+?)_/g, "<em>$1</em>")

      // 链接
      text = text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>')

      return text
    }

    for (const line of lines) {
      // 代码块占位符
      if (line.match(/\{\{CODE_BLOCK_(\d+)\}\}/)) {
        if (inList) flushList()
        inList = false
        const idx = parseInt(line.match(/\{\{CODE_BLOCK_(\d+)\}\}/)[1])
        const { lang, code } = codeBlocks[idx]
        htmlLines.push(`<pre><code class="language-${lang}">${this.escapeHtml(code.trim())}</code></pre>`)
        continue
      }

      // 标题
      const headerMatch = line.match(/^(#{1,6})\s+(.+?)(?:\s+\{#([A-Za-z0-9\-_]+)\})?$/)
      if (headerMatch) {
        if (inList) flushList()
        inList = false
        const level = headerMatch[1].length
        const rawTitle = headerMatch[2]
        const customId = headerMatch[3]
        const content = processInline(rawTitle)
        const id = customId || this.slugify(rawTitle)
        htmlLines.push(`<h${level} id="${id}">${content}</h${level}>`)
        continue
      }

      // 水平线
      if (line.match(/^(-{3,}|\*{3,}|_{3,})$/)) {
        if (inList) flushList()
        inList = false
        htmlLines.push("<hr>")
        continue
      }

      // 无序列表
      const ulMatch = line.match(/^[\s]*[-*+]\s+(.+)$/)
      if (ulMatch) {
        if (!inList || isOrderedList) {
          if (inList) flushList()
          isOrderedList = false
        }
        inList = true
        listBuffer.push(processInline(ulMatch[1]))
        continue
      }

      // 有序列表
      const olMatch = line.match(/^[\s]*\d+\.\s+(.+)$/)
      if (olMatch) {
        if (!inList || !isOrderedList) {
          if (inList) flushList()
          isOrderedList = true
        }
        inList = true
        listBuffer.push(processInline(olMatch[1]))
        continue
      }

      // 引用
      const quoteMatch = line.match(/^>\s*(.*)$/)
      if (quoteMatch) {
        if (inList) flushList()
        inList = false
        htmlLines.push(`<blockquote><p>${processInline(quoteMatch[1])}</p></blockquote>`)
        continue
      }

      // 空行
      if (line.trim() === "") {
        if (inList) flushList()
        inList = false
        continue
      }

      // 普通段落
      if (inList) flushList()
      inList = false
      htmlLines.push(`<p>${processInline(line)}</p>`)
    }

    if (inList) flushList()

    return htmlLines.join("\n")
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  sanitizeHtml(html) {
    if (!html) return ""

    const allowedTags = new Set([
      "H1", "H2", "H3", "H4", "H5", "H6",
      "P", "BR", "HR", "STRONG", "EM", "A",
      "UL", "OL", "LI", "BLOCKQUOTE",
      "CODE", "PRE", "TABLE", "THEAD", "TBODY", "TR", "TH", "TD"
    ])
    const allowedAttrs = new Set([ "href", "rel", "target", "class", "id" ])
    const allowedProtocols = [ "http:", "https:", "mailto:", "tel:" ]

    const parser = new DOMParser()
    const doc = parser.parseFromString(html, "text/html")

    const cleanseNode = (node) => {
      // Remove disallowed nodes
      if (node.nodeType === Node.ELEMENT_NODE && !allowedTags.has(node.tagName)) {
        node.remove()
        return
      }

      if (node.nodeType === Node.ELEMENT_NODE) {
        // Strip unexpected attributes
        for (const attr of Array.from(node.attributes)) {
          if (!allowedAttrs.has(attr.name)) {
            node.removeAttribute(attr.name)
            continue
          }

          if (attr.name === "href") {
            try {
              const rawHref = attr.value || ""
              const isHash = rawHref.startsWith("#")
              const url = new URL(rawHref, window.location.href)
              const protocolAllowed = allowedProtocols.includes(url.protocol)
              if (!protocolAllowed && !isHash) {
                node.removeAttribute("href")
                continue
              }

              const isExternal = url.origin !== window.location.origin && !isHash
              if (isExternal) {
                node.setAttribute("rel", "noopener noreferrer")
                node.setAttribute("target", "_blank")
              } else {
                node.removeAttribute("target")
                node.removeAttribute("rel")
              }
            } catch (_) {
              node.removeAttribute("href")
            }
          }
        }
      }

      for (const child of Array.from(node.childNodes)) {
        cleanseNode(child)
      }
    }

    cleanseNode(doc.body)
    return doc.body.innerHTML
  }

  slugify(text) {
    return (text || "")
      .toLowerCase()
      .replace(/[^\w\u4e00-\u9fff]+/g, "-")
      .replace(/^-+|-+$/g, "")
  }
}
